import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { logoutProcessor } from "./helpers/logoutProcessor";
import { playSuccess, playError } from "./helpers/audioPlayer";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import HiddenScanInput from "./HiddenScanInput";
import ScanStatusIcons from "./ScanStatusIcons";
import ScanFlashOverlay from "./ScanFlashOverlay";
import FitWord from "./FitWord";
import { conveyorLogger } from "./helpers/logHandler";
import { zoneDispatchReasonKey } from "./sorterTwo/zoneDispatchReasons";
import { apiFetch } from "../api/apiFetch";

type ConveyorAddPalletProps = {
    goBack: () => void;
};

const ConveyorAddPallet = ({ goBack }: ConveyorAddPalletProps) => {
    const navigator = useNavigate();
    const { t } = useTranslation();

    const userData = window.localStorage.getItem("user");
    const userObject = userData !== null ? JSON.parse(userData) : { warehouse: "Newport" };
    const warehouse = userObject.warehouse || "Newport";

    const [step, setStep] = useState<number>(1);
    const [scan, setScan] = useState<string>("");
    const [palletId, setPalletId] = useState<string>("");
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [scanStatus, set_scanStatus] = useState<"idle" | "success" | "error">("idle");
    const [unassignedChutes, setUnassignedChutes] = useState<string[]>([]);
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);

    const fetchUnassignedChutes = useCallback(async () => {
        try {
            const res = await apiFetch(`/conveyor/chutes/pallet/unassigned`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse }),
            });
            if (res.ok) {
                const data = await res.json();
                const chutes = Array.isArray(data)
                    ? data
                        .map((entry: any) => entry.chute)
                        .filter((chute: string) => chute && !["1", "2"].includes(chute))
                        .sort((a: string, b: string) => Number(a) - Number(b))
                    : [];
                setUnassignedChutes(chutes);
            }
        } catch {
            // silently ignore — list just won't update
        }
    }, [warehouse]);

    useEffect(() => {
        fetchUnassignedChutes();
        const interval = setInterval(fetchUnassignedChutes, 5000);
        return () => clearInterval(interval);
    }, [fetchUnassignedChutes]);

    const flashScanStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        set_scanStatus(status);
        statusTimerRef.current = setTimeout(() => set_scanStatus("idle"), 500);
    };

    const doScanPallet = useCallback((scannedValue?: string) => {
        const val = scannedValue ?? scan;
        if (val.trim() === "") return;

        // Check if pallet is valid
        if (!/(^UK|PL|TT)-[0-9]{4}$/.test(val)) {
            flashScanStatus("error");
            toast.dismiss(); toast.error(t("invalid_pallet"));
            playError();
            conveyorLogger("Newport", "Scanned Pallet", val, "Warehouse", "Hand", "error");
            return;
        }

        setPalletId(val);
        setScan("");
        conveyorLogger("Newport", "Scanned Pallet", val, "Warehouse", "Hand", "success");
        setStep(2);
    }, [scan]);

    const doScanChute = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? scan;
        if (val.trim() === "") return;

        const chute = val;

        // Check if pallet is valid
        if (!/(^NP|PL|TT)[0-9]{3}$/.test(val)) {
            flashScanStatus("error");
            toast.dismiss(); toast.error(t("invalid_chute"));
            playError();
            conveyorLogger("Newport", "Scanned Chute", chute, "Warehouse", "Hand", "error");
            return;
        }

        conveyorLogger("Newport", "Scanned Chute", chute, "Warehouse", "Hand", "success");

        setScan("");
        setSubmitting(true);

        await apiFetch(`/conveyor/chutes/pallet`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                warehouse: palletId.startsWith("UK") ? "Newport" : palletId.startsWith("PL") ? "Poland" : "Test",
                palletId: palletId,
                chute: Number(chute.slice(2)).toString()
            })
        }).then(async (res) => {
            if (res.ok) {
                flashScanStatus("success");
                playSuccess();
                setPalletId("");
                conveyorLogger("Newport", "Add Pallet", palletId, "Warehouse", `Chute ${chute}`, "success");
                fetchUnassignedChutes();
                setStep(1);
            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
            } else {
                // With the warehouse's Portal reauction move on, the BE refuses a
                // pallet that is not a free Portal tote: { ok:false, reason }.
                // Name the reason and go back to the pallet scan.
                let body: any = null;
                try { body = await res.json(); } catch { body = null; }
                flashScanStatus("error");
                playError();
                toast.dismiss();
                if (body && body.ok === false && body.reason) {
                    toast.error(t(zoneDispatchReasonKey(body.reason), { status: body.status ?? "", order: "", tote: palletId }));
                    conveyorLogger("Newport", "Add Pallet", palletId, "Warehouse", `Chute ${chute}`, body.reason);
                    setPalletId("");
                    setStep(1);
                } else {
                    toast.error(t("add_failed"));
                    conveyorLogger("Newport", "Add Pallet", palletId, "Warehouse", `Chute ${chute}`, "error");
                }
            }
        }).catch(() => {
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("add_failed"));
            conveyorLogger("Newport", "Add Pallet", palletId, "Warehouse", `Chute ${chute}`, "error");
        });

        setSubmitting(false);
    }, [scan, palletId, navigator, t, fetchUnassignedChutes]);

    const handleSubmit = useCallback((scannedValue?: string) => {
        if (step === 1) {
            doScanPallet(scannedValue);
        } else {
            doScanChute(scannedValue);
        }
    }, [step, doScanPallet, doScanChute]);

    return (
        <div className="scan-panel" style={{ backgroundColor: scanStatus === "success" ? "rgba(34, 197, 94, 0.18)" : undefined }}>
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("add_pallet")}</h1>
                <button className="page-back-btn" onClick={goBack}><ArrowLeft size={20} /></button>
            </div>

            <div className="scan-centre">
                {step === 1 && <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />}

                {step === 1 && (
                    <div className="conveyor-scan-group">
                        <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {t("scan_pallet_id").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="conveyor-scan-group">
                        <div className={`scan-command ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                            {t("scan_chute").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                        <div className={`scan-feedback-area ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                            <p className="conveyor-scan-subtitle">{t("pallet_label", { id: palletId })}</p>
                            {submitting && <p className="conveyor-status-text">{t("submitting")}</p>}
                        </div>
                    </div>
                )}
            </div>
            {step === 2 && scanStatus === "success" && (
                <div className="s2-inline-status s2-inline-success"><CheckCircle size={140} strokeWidth={3} /></div>
            )}

            <div className="conveyor-chute-list">
                <p className="conveyor-chute-list-title">{t("chutes_needing_pallets")}</p>
                {unassignedChutes.length > 0 ? (
                    <div className="conveyor-chute-list-grid">
                        {unassignedChutes.slice(0, 3).map((chute) => (
                            <span key={chute} className="conveyor-chute-chip">{chute}</span>
                        ))}
                        {unassignedChutes.length > 3 && (
                            <span className="conveyor-chute-chip conveyor-chute-chip-more">…</span>
                        )}
                    </div>
                ) : (
                    <p className="conveyor-chute-list-empty">{t("no_chutes_needing_pallets")}</p>
                )}
            </div>

            <HiddenScanInput
                value={scan}
                onChange={setScan}
                onSubmit={handleSubmit}
                disabled={submitting}
            />
        </div>
    );
};

export default ConveyorAddPallet;
